import type { ReactNode } from 'react'
import type { Army, FeedbackKind, GameEndReason, GameMode, Locale, MatchEnd, MatchStatus, PlayerView, Tier, TournamentStatus } from '@chess-uno/shared-protocol'

/** Plural of a counted word, French rule (0 and 1 are singular). */
function n(count: number, word: string, plural = `${word}s`): string {
  return `${count} ${count > 1 ? plural : word}`
}

export const fr = {
  common: {
    back: 'Retour',
    home: 'Accueil',
    cancel: 'Annuler',
    loading: 'Chargement…',
    error: 'Erreur',
    yes: 'Oui',
    no: 'Non',
    armies: { w: 'Blancs', b: 'Noirs' } as Record<Army, string>,
    tiers: { bronze: 'Bronze', silver: 'Argent', gold: 'Or', platinum: 'Platine', diamond: 'Diamant' } as Record<Tier, string>,
  },

  /** Game modes: the name of the capture mode is set here (and in the rules documents). */
  modes: {
    purist: {
      name: 'Puriste',
      tagline: 'Échec et mat',
      desc: 'Comme aux échecs : jamais de roi laissé en échec, le roi ne se prend pas, on gagne par échec et mat.',
      note: 'Mode Puriste : on ne peut pas laisser son roi en échec ; victoire par échec et mat.',
    },
    capture: {
      name: 'Chasse au roi',
      tagline: 'Prise du roi',
      desc: 'Les règles d’origine : on peut laisser son roi attaqué, et l’on gagne en le prenant.',
      note: 'Mode Chasse au roi : un roi peut rester attaqué ; on gagne en le prenant.',
    },
  } as Record<GameMode, { name: string; tagline: string; desc: string; note: string }>,

  /** The "Beat the machine" Instagram challenge (November 2026), in a small note on the entry pages. */
  campaign: {
    eyebrow: 'Défi de novembre',
    title: 'Beat the machine',
    text: 'Battez l’ordinateur en niveau Moyen et partagez votre victoire sur Instagram : une carte cadeau de 20 € est tirée au sort chaque lundi.',
    link: 'Tous les détails sur Instagram',
    dismiss: 'Masquer',
  },

  language: {
    /** Name of each language, written in that language. */
    names: { fr: 'Français', en: 'English', es: 'Español', de: 'Deutsch', hi: 'हिन्दी' } as Record<Locale, string>,
    /** Label of the language menu. */
    menu: 'Langue',
    hint: 'Ce site existe aussi en français.',
    hintLink: 'Version française',
    dismiss: 'Fermer',
  },

  chrome: {
    homeAria: 'Chess-Uno, accueil',
    rules: 'Règles',
    viewProfile: 'Voir mon profil',
    soundOn: 'Activer le son',
    soundOff: 'Désactiver le son',
    logout: 'Se déconnecter',
    legal: 'Mentions légales',
    privacy: 'Confidentialité',
    instagram: 'Chess-Uno sur Instagram',
    facebook: 'Chess-Uno sur Facebook',
  },

  feedback: {
    link: 'Avis & suggestions',
    title: 'Votre avis compte',
    lead: 'Une idée, un bug, un compliment ? Un message suffit : il est lu par l’éditeur du jeu.',
    close: 'Fermer',
    kindLabel: 'Sujet',
    kinds: { idea: 'Idée', bug: 'Bug', praise: 'J’aime', other: 'Autre' } as Record<FeedbackKind, string>,
    messageLabel: 'Votre message',
    placeholders: {
      idea: 'Qu’aimeriez-vous trouver dans Chess-Uno ?',
      bug: 'Que s’est-il passé ? Sur quelle page, avec quel appareil ?',
      praise: 'Qu’est-ce qui vous plaît le plus ?',
      other: 'Écrivez ce que vous voulez nous dire.',
    } as Record<FeedbackKind, string>,
    contactLabel: 'Pour vous répondre (facultatif)',
    contactPlaceholder: 'E-mail ou pseudo Telegram',
    fine: (privacy: (label: string) => ReactNode) => (
      <>
        Votre message est lu par l’éditeur, avec votre pseudo si vous êtes connecté ({privacy('confidentialité')}).
      </>
    ),
    send: 'Envoyer',
    sending: 'Envoi…',
    thanksTitle: 'Merci !',
    thanksText: 'Votre message est bien arrivé. Il aide à améliorer le jeu.',
    rateGame: 'Cette partie vous a plu ?',
    starLabel: (count: number) => `${count} sur 5`,
    rated: 'Merci pour votre note !',
    tellMore: 'Dire pourquoi',
  },

  toast: {
    aria: (name: string) => `Défi de ${name}`,
    challengesYou: (name: ReactNode) => (
      <>
        <strong>{name}</strong> vous défie
      </>
    ),
    accept: 'Accepter le duel',
    later: 'Plus tard',
  },

  auth: {
    login: 'Se connecter',
    register: 'Créer un compte',
    username: 'Pseudo',
    password: 'Mot de passe',
    registerFine: (privacy: (label: string) => ReactNode) => (
      <>
        Pseudo de 3 à 20 caractères, mot de passe de 6 caractères minimum. Votre pseudo et vos résultats apparaissent dans le classement
        public ({privacy('confidentialité')}).
      </>
    ),
    submitRegister: 'Créer mon compte',
    submitLogin: 'Se connecter',
  },

  landing: {
    eyebrow: 'Chess-Uno · jeu d’échecs et cartes en ligne',
    title: 'Jouez aux échecs avec des cartes.',
    titleEm: 'Chaque tour change la partie.',
    lead:
      'Dans Chess-Uno, vous jouez aux échecs avec des cartes révélées au fil de la partie. Chaque tour change vos options : nombre de déplacements, pions disponibles ou armée contrôlée. Gagnez par échec et mat, ou en prenant le roi adverse dans le mode Chasse au roi. Défiez un ami, trouvez un adversaire classé ou entraînez-vous contre l’ordinateur.',
    readRules: 'Lire les règles',
    leaderboard: 'Classement',
    howEyebrow: 'Comment jouer',
    howTitle: 'Une partie d’échecs, des cartes qui changent les coups',
    howP1:
      'Le plateau et les pièces suivent les déplacements des échecs. Au début de chaque tour, une carte est piochée et révélée à tous : elle peut donner plusieurs déplacements, passer un tour, rendre des pions ou inverser les armées. Deux modes : Puriste, où l’on gagne par échec et mat, et Chasse au roi, où l’on gagne en prenant le roi adverse.',
    howP2:
      'Vous ne gardez aucune carte en main. Une carte chiffrée vous accorde autant de déplacements que sa valeur ; vous pouvez arrêter cette série après le premier coup. Les cartes +2 et +4 permettent de replacer des pions capturés. Avec Inversion, les joueurs échangent leurs armées sans modifier la position des pièces. Un Joker autorise une pièce à se déplacer comme une dame pendant le tour. Les deux joueurs voient toujours la carte révélée.',
    discoverRules: 'Découvrir les règles de Chess-Uno',
    playEyebrow: 'Jouer en ligne',
    playTitle: 'Entre amis, en partie classée ou contre l’ordinateur',
    playP1:
      'Créez un duel privé et partagez son lien, rejoignez la file pour une partie classée avec Elo, ou essayez les cartes contre l’ordinateur. Les parties contre l’ordinateur ne modifient pas votre classement.',
    playP2:
      'Après avoir créé un compte, vous pouvez inviter un ami avec un lien privé ou chercher automatiquement un adversaire. Les parties terminées entre deux comptes font évoluer votre Elo ; le classement affiche aussi les résultats récents et vos confrontations directes. Pour découvrir les effets sans enjeu, lancez un entraînement contre l’ordinateur. Avant un duel privé, vous pouvez choisir les cartes présentes dans la pioche et régler le nombre maximal des cartes chiffrées.',
    heroAlt: 'Un pion d’échecs devant des cartes',
    faqEyebrow: 'Questions fréquentes',
    faqTitle: 'Tout savoir sur Chess-Uno',
    /** Also used for the FAQPage structured data and llms.txt: keep the answers factual. */
    faq: [
      {
        q: 'Qu’est-ce que Chess-Uno ?',
        a: 'Chess-Uno est une variante des échecs jouable gratuitement en ligne, dans le navigateur. On joue sur un échiquier classique avec les pièces habituelles, mais au début de chaque tour une carte est piochée et révélée aux deux joueurs : elle fixe ce que le joueur peut faire pendant ce tour.',
      },
      {
        q: 'Comment gagne-t-on une partie ?',
        a: 'Cela dépend du mode choisi avant la partie. En mode Puriste, comme aux échecs, on ne peut jamais laisser son roi en échec et l’on gagne par échec et mat : quand l’adversaire ne peut pas parer l’échec avec la carte qu’il vient de piocher. En mode Chasse au roi, on peut laisser son roi attaqué, et l’on gagne en prenant le roi adverse.',
      },
      {
        q: 'Que font les cartes ?',
        a: 'Une carte chiffrée donne autant de déplacements que sa valeur (de 1 à 5 par défaut). « Passe ton tour » fait passer le tour. Les +2 et +4 rendent des pions capturés à replacer sur l’échiquier. L’Inversion échange les armées des deux joueurs. Le Joker permet de déplacer une pièce comme une dame. Aucun joueur ne garde de carte en main.',
      },
      {
        q: 'Peut-on jouer seul ?',
        a: 'Oui, contre l’ordinateur, en niveau Facile ou Moyen. Ces parties d’entraînement ne comptent pas pour le classement. On peut aussi défier un ami avec un lien privé ou trouver un adversaire dans la file des parties classées.',
      },
      {
        q: 'Comment fonctionne le classement ?',
        a: 'Chaque joueur démarre à 1000 Elo. Seules les parties terminées entre deux comptes sont classées. Les paliers vont de Bronze à Diamant (1400 Elo et plus), et les confrontations directes entre joueurs sont publiques.',
      },
      {
        q: 'Faut-il payer ou installer quelque chose ?',
        a: 'Non. Chess-Uno est gratuit et se joue dans le navigateur, sur ordinateur comme sur mobile. Il suffit de créer un compte avec un pseudo et un mot de passe, sans adresse e-mail.',
      },
      {
        q: 'Chess-Uno est-il lié au jeu UNO ?',
        a: 'Non. Chess-Uno est un jeu indépendant, ni affilié, ni approuvé, ni sponsorisé par Mattel, titulaire de la marque UNO.',
      },
    ] as Array<{ q: string; a: string }>,
  },

  home: {
    greeting: (hour: number): string => (hour < 5 || hour >= 18 ? 'Bonsoir' : 'Bonjour'),
    play: 'Jouer',
    ranked: 'Partie classée',
    rankedDetail: 'Adversaire trouvé automatiquement',
    challengeFriend: 'Défier un ami',
    challengeFriendDetail: 'Lien privé à partager',
    join: 'Rejoindre',
    joinDetail: 'Avec un code d’invitation',
    training: 'Entraînement',
    trainingDetail: 'Contre l’IA, non classé',
    tournament: 'Tournoi',
    tournamentDetail: 'Élimination directe, avec un code',
    tournamentOngoing: (name: string) => `Tournoi en cours : ${name}`,
    tournamentOpen: (name: string) => `Inscriptions ouvertes : ${name}`,
    rules: 'Règles du jeu',
    waitingGame: 'Partie en attente d’un adversaire',
    ongoingAgainst: (name: string) => `Partie en cours contre ${name}`,
    resume: 'Reprendre',
    leaderboard: 'Classement général',
    recentResults: 'Derniers résultats',
    headToHead: 'Face-à-face',
    rivalries: 'Grandes rivalités',
  },

  join: {
    eyebrow: 'Invitation',
    title: 'Rejoindre une partie',
    lead: 'Saisissez le code reçu de votre adversaire.',
    code: 'Code d’invitation',
    submit: 'Rejoindre la partie',
  },

  invite: {
    challengesYou: (name: ReactNode) => (
      <>
        <strong>{name}</strong> vous défie
      </>
    ),
    invited: 'Vous êtes invité à un duel',
  },

  gate: {
    title: 'Connexion',
    lead: 'Connectez-vous pour jouer et grimper au classement.',
  },

  queue: {
    searching: 'Recherche d’un adversaire…',
    chooseTitle: 'Choisissez votre mode',
    chooseLead: 'Vous affronterez un joueur qui a choisi le même mode. Le classement Elo est commun aux deux modes.',
    changeMode: 'Changer de mode',
    eyebrow: 'Partie classée',
    lead: 'La partie démarre dès qu’un joueur rejoint la file.',
  },

  competition: {
    justNow: 'à l’instant',
    minutesAgo: (m: number) => `il y a ${m} min`,
    hoursAgo: (h: number) => `il y a ${h} h`,
    daysAgo: (d: number) => `il y a ${d} j`,
    tierTitle: (label: string) => `Palier ${label}`,
    streakTitle: (count: number) => `${n(count, 'victoire')} d’affilée`,
    form: { W: 'Victoire', L: 'Défaite', D: 'Nulle' },
    formAria: (items: string[]) => `Forme : ${items.join(', ')}`,
    yourRanking: 'Votre classement',
    outOf: (count: number) => `sur ${n(count, 'joueur classé', 'joueurs classés')}`,
    unranked: 'Non classé — jouez une partie classée',
    pointsFor: (missing: number, label: string) => `Encore ${missing} points pour ${label}`,
    maxTier: 'Palier maximal',
    pointsBefore: (missing: number, label: string) => (
      <>
        <b>{missing}</b> points avant {label}
      </>
    ),
    maxTierCaption: 'Palier maximal — défendez votre place',
    wins: 'Victoires',
    losses: 'Défaites',
    draws: 'Nulles',
    winRate: 'Réussite',
    percent: (value: number) => `${value} %`,
    streak: 'Série',
    best: (count: number) => `record ${count}`,
    leaderboardEmpty: 'Personne n’est encore classé. Gagnez une partie contre un ami pour ouvrir le classement.',
    you: 'vous',
    record: (w: number, l: number, d: number) => `${w} V · ${l} D${d ? ` · ${d} N` : ''}`,
    challenge: 'Défier',
    taunts: {
      never: 'Jamais affronté : à vous d’ouvrir le score.',
      twoLosses: 'Deux défaites de suite. La revanche s’impose.',
      dominate: 'Vous dominez ce duel.',
      lead: 'Vous menez, rien n’est joué.',
      tied: 'À égalité parfaite.',
      escape: 'Ce duel vous échappe.',
      behind: 'Vous êtes mené.',
    },
    rivalsEmpty: 'Aucune confrontation pour l’instant. Défiez un joueur du classement.',
    revenge: 'Revanche',
    replay: 'Rejouer',
    drawsShort: (d: number) => `${d} N`,
    rivalriesEmpty: 'Une rivalité apparaît dès que deux joueurs se sont affrontés deux fois.',
    rivalryMeta: (games: number, draws: number) => `${n(games, 'partie')}${draws ? ` · ${n(draws, 'nulle')}` : ''}`,
    recentEmpty: 'Aucune partie classée pour l’instant.',
    and: 'et',
    drew: 'font nulle',
    beat: 'bat',
    reasons: {
      king_captured: 'roi capturé',
      checkmate: 'échec et mat',
      resign: 'abandon',
      abandon: 'déconnexion',
      draw_agreed: 'nulle acceptée',
      no_move_limit: 'nulle par inactivité',
    } as Record<GameEndReason, string>,
  },

  settings: {
    decrease: 'Diminuer',
    increase: 'Augmenter',
    maxNumberAria: 'Numéro maximal des cartes chiffrées',
    keepCards: (max: number) => `Garder les cartes 1 à ${max}`,
    eyebrowBot: 'Entraînement · non classé',
    eyebrowPrivate: 'Partie privée · classée',
    title: 'Paramètres de la partie',
    leadBot: 'Réglez l’adversaire et le paquet, puis lancez la partie.',
    leadPrivate: 'Réglez le paquet ; un lien d’invitation est créé pour votre adversaire.',
    opponent: 'Adversaire',
    opponentHint: 'Niveaux expérimentaux : l’IA dispose de plus ou moins de temps de réflexion.',
    easy: 'Facile',
    easyDesc: 'Anticipe un coup adverse.',
    medium: 'Moyen',
    mediumDesc: 'Anticipe deux coups, réfléchit plus longtemps.',
    side: 'Votre camp',
    sideHint: 'Une Inversion peut échanger les armées en cours de partie.',
    whiteDesc: 'Vous commencez',
    blackDesc: 'L’IA commence',
    random: 'Au hasard',
    randomSummary: 'Tirage au sort',
    rules: 'Règles',
    rulesHint: 'Deux façons de jouer : comme aux échecs, ou en chassant le roi.',
    deck: 'Paquet',
    deckHint: 'Une carte chiffrée donne autant de déplacements que sa valeur.',
    numberCards: 'Cartes chiffrées',
    fromOneTo: (max: number) => (
      <>
        de 1 à <b>{max}</b>
      </>
    ),
    jokers: 'Jokers',
    jokersDesc: 'Un déplacement, où n’importe quelle pièce bouge comme une dame.',
    advanced: 'Règles avancées',
    advancedHint: 'Récupération de pions, garde-fou, nulle.',
    plus2: 'Pions rendus par un +2',
    plus4: 'Pions rendus par un +4',
    drawAfter: 'Nulle après N tours sans déplacement',
    safeguard: 'Garde-fou des séries',
    safeguardDesc: 'Une série s’arrête dès que le roi adverse est attaqué.',
    summary: 'Récapitulatif',
    mode: 'Mode',
    modeBot: 'Contre l’IA',
    modePrivate: 'Lien privé',
    level: 'Niveau',
    camp: 'Camp',
    deckSize: (count: number) => `${count} cartes`,
    numbers: 'Chiffres',
    range: (max: number) => `1 à ${max}`,
    jokersIncluded: 'Jokers',
    ranking: 'Classement',
    unranked: 'Non classée',
    eloAtStake: 'Elo en jeu',
    start: 'Commencer la partie',
    create: 'Créer la partie',
    locked: 'Les règles sont figées au lancement de la partie.',
  },

  game: {
    drawAgreed: 'Partie nulle (accord mutuel).',
    drawIdle: 'Partie nulle (tours sans déplacement).',
    result: (mine: boolean, winner: string, reason: GameEndReason) => {
      const why: Partial<Record<GameEndReason, string>> = {
        king_captured: 'capture du roi',
        checkmate: 'échec et mat',
        resign: 'abandon',
        abandon: 'déconnexion prolongée',
      }
      return `${mine ? 'Victoire' : 'Défaite'} — ${winner} gagne par ${why[reason] ?? reason}.`
    },
    headToHead: 'Face-à-face',
    firstDuel: 'Premier duel',
    youLead: 'Vous menez',
    leads: (name: string) => `${name} mène`,
    tied: 'Égalité',
    training: (level: string) => `Partie d’entraînement contre l’IA (niveau ${level}) : non classée.`,
    computing: 'Calcul du classement…',
    promotion: (tier: string) => `Promotion : palier ${tier}`,
    demotion: (tier: string) => `Relégation en ${tier}… la revanche s’impose.`,
    recordAgainst: (name: string) => `Bilan contre ${name} :`,
    streak: (count: number) => `${n(count, 'victoire')} d’affilée`,
    backHome: 'Retour à l’accueil',
    privateGame: 'Partie privée',
    waiting: 'En attente d’un adversaire…',
    shareLink: 'Partagez ce lien :',
    copied: 'Copié !',
    copyLink: 'Copier le lien',
    inviteText: (name: string) => `${name} te défie sur Chess-Uno ! Accepte le duel :`,
    messageCopied: 'Message copié !',
    sent: 'Envoyé !',
    sendChallenge: 'Envoyer le défi',
    code: 'Code :',
    notified: 'Le joueur défié reçoit aussi une notification s’il est en ligne.',
    loadingGame: 'Chargement de la partie…',
    connecting: '(connexion au serveur…)',
    connected: 'Connecté',
    reconnecting: 'Reconnexion…',
    acceptDraw: 'Accepter la nulle',
    decline: 'Refuser',
    offerDraw: 'Proposer la nulle',
    offerDrawShort: 'Nulle',
    offerDrawConfirm: 'Proposer la nulle à votre adversaire ?',
    acceptDrawShort: 'Accepter',
    resignConfirm: 'Abandonner la partie ?',
    resign: 'Abandonner',
    resultAria: 'Résultat',
    rematchOffered: (name: string) => `${name} propose une revanche.`,
    rematchWaiting: 'Revanche proposée : en attente de l’adversaire…',
    rematch: 'Revanche',
    closeResult: 'Fermer pour voir l’échiquier',
    showResult: 'Voir le résultat',
    verdicts: { win: 'Victoire', loss: 'Défaite', draw: 'Partie nulle' },
    winText: (opponent: string, gain: number, rating: number) =>
      `J’ai battu ${opponent} sur Chess-Uno (+${gain} Elo, ${rating}). À qui le tour ?`,
    shared: 'Partagé !',
    shareWin: 'Partager ma victoire',
  },

  panels: {
    phases: {
      turn_start: 'Début de tour',
      draw: 'Pioche',
      effect_choice: 'Choix de l’effet (Joker)',
      place_pawns: 'Placement des pions récupérés',
      moves: 'Déplacements',
      turn_end: 'Fin de tour',
      game_over: 'Partie terminée',
    } as Record<PlayerView['phase'], string>,
    playsArmy: (army: Army) => `Joue les ${army === 'w' ? 'blancs' : 'noirs'}`,
    chooseMoves: (you: boolean): string => (you ? 'Choisissez votre nombre de déplacements' : 'choisit son nombre de déplacements'),
    placePawns: (you: boolean, count: number) => (you ? `Replacez ${n(count, 'pion')} (cases vertes)` : `replace ${n(count, 'pion')}`),
    joker: (you: boolean): string => (you ? 'Joker : déplacez n’importe quelle pièce comme une dame' : 'Joker : déplace une pièce comme une dame'),
    moreMoves: (you: boolean, count: number) =>
      you ? `Encore ${n(count, 'déplacement')} (ou terminez la série)` : `encore ${n(count, 'déplacement')}`,
    playMoves: (you: boolean, count: number) => (you ? `Jouez ${n(count, 'déplacement')}` : `joue ${n(count, 'déplacement')}`),
    pipsAria: (made: number, total: number) => `${made} déplacement(s) joué(s) sur ${total}`,
    yourTurn: 'À vous de jouer',
    turnOf: (name: string) => `Tour de ${name}`,
    check: 'Échec !',
    go: 'Partez !',
    gameStarts: 'La partie commence',
    drawing: (you: boolean, summary: string) => `${you ? 'Vous piochez' : 'pioche'} : ${summary}`,
    inCheckYou: (mode: GameMode): string =>
      mode === 'purist' ? 'Votre roi est en échec : parez avec votre carte.' : 'Votre roi est attaqué : parez avec votre carte.',
    inCheckOpponent: (mode: GameMode): string =>
      mode === 'purist' ? 'Roi adverse en échec : sans parade, c’est échec et mat.' : 'Roi adverse attaqué : sans parade, il sera pris.',
    turnNumber: (turn: number) => `Tour ${turn}`,
    discardTitle: (owner: string, count: number) => `Défausse de ${owner} : ${count} carte(s)`,
    discard: 'Défausse',
    seatAria: (name: string) => `Joueur ${name}`,
    connected: 'Connecté',
    disconnected: 'Déconnecté',
    you: '(vous)',
    ai: 'IA',
    eloTitle: (tier: string) => `Classement Elo · ${tier}`,
    seatYourTurn: 'à vous',
    seatPlaying: 'joue',
    pawnsTitle: 'Pions capturés récupérables par un +2 ou un +4',
    toRecover: (count: number) => `${count} à récupérer`,
    turnCardAria: 'Carte du tour',
    drawPile: 'Pioche',
    youDrew: 'Vous avez pioché',
    drew: (name: string) => `${name} a pioché`,
    revealed: (label: string) => `Révélée : ${label}`,
    noCard: 'Aucune carte',
    jokerChoiceAria: 'Choix du nombre de déplacements',
    jokerChoice: 'Joker : combien de déplacements ce tour ?',
    placementHint: (count: number) => `Cliquez une case verte du plateau pour replacer un pion (${count} restant(s)).`,
    endSeries: (count: number) => `Terminer la série (${n(count, 'déplacement')} non joué${count > 1 ? 's' : ''})`,
    effects: {
      number: (count: number) => `${n(count, 'déplacement')} à jouer.`,
      zero: 'Tour passé sans déplacement.',
      skip: 'Passe ton tour : l’adversaire pioche aussitôt et joue sa carte.',
      reverse: 'Inversion : les joueurs échangent leurs armées ; le joueur courant continue avec la nouvelle.',
      plus: (card: string, pawns: number, moves: number) => `${card} : récupère jusqu’à ${pawns} pions capturés, puis ${moves} déplacement(s).`,
      wild: 'Joker : un déplacement, avec n’importe quelle pièce qui se déplace comme une dame.',
    },
    reveal: {
      who: (you: boolean, name: string) => (you ? 'Vous piochez' : `${name} pioche`),
      moves: (count: number) => n(count, 'déplacement'),
      zero: 'Tour passé',
      zeroDetail: 'Aucun déplacement ce tour.',
      skip: 'Passe son tour',
      skipDetail: (name: string) => `${name} pioche et joue.`,
      reverse: 'Inversion !',
      reverseDetail: 'Les joueurs échangent leurs armées.',
      recovers: (count: number) => `Récupère ${n(count, 'pion')}`,
      then: (count: number) => `Puis ${n(count, 'déplacement')}.`,
      oneMove: '1 déplacement',
      noPawn: 'Aucun pion à récupérer.',
      joker: 'Joker',
      jokerDetail: 'Une pièce au choix se déplace comme une dame.',
    },
    yourKingAttacked: 'Votre roi est attaqué',
    enemyKingAttacked: 'Le roi adverse est attaqué',
    youWin: 'Vous gagnez',
    wins: (name: string) => `${name} gagne`,
    /** Pieces with their article ("le fou", "la tour"). */
    pieces: { p: 'le pion', n: 'le cavalier', b: 'le fou', r: 'la tour', q: 'la dame', k: 'le roi' } as Record<string, string>,
    somePiece: 'la pièce',
    deliversMate: (piece: string, from: string) => `${piece} en ${from} donne échec et mat`,
    takesKing: (piece: string, from: string, to: string) => `${piece} en ${from} prend le roi en ${to}`,
    kingTakenOn: (square: string) => `Le roi est pris en ${square}`,
    checkmate: 'Échec et mat',
    kingCaptured: 'Roi pris',
    statusAria: 'État de la partie',
    phase: 'Phase',
    actor: 'Joueur autorisé à agir',
    movesRemaining: 'Déplacements restants',
    movesValue: (remaining: number, made: number) => `${remaining} (effectués : ${made})`,
    idleTurns: 'Tours sans déplacement',
    carefulKing: 'Roi prudent : le roi ne peut pas aller sur une case attaquée.',
    kingAttacked: (army: Army, mode: GameMode): string =>
      mode === 'purist'
        ? `Le roi ${army === 'w' ? 'blanc' : 'noir'} est en échec.`
        : `Le roi ${army === 'w' ? 'blanc' : 'noir'} est attaqué (information, rien n’oblige à réagir).`,
    youOfferedDraw: 'Vous avez proposé la nulle.',
    offersDraw: (name: string) => `${name} propose la nulle.`,
    log: 'Journal',
  },

  board: {
    promotionAria: 'Choix de promotion',
    promotion: 'Promotion :',
    pieces: { q: 'Dame', r: 'Tour', b: 'Fou', n: 'Cavalier' },
  },

  card: {
    back: 'Dos de carte',
  },

  tournament: {
    eyebrow: 'Tournoi',
    hubTitle: 'Tournoi à élimination directe',
    hubLead: 'Chaque défaite vous élimine : le dernier joueur en lice remporte le tournoi.',
    joinTitle: 'Rejoindre un tournoi',
    joinCode: 'Code du tournoi',
    joinSubmit: 'Rejoindre',
    createTitle: 'Organiser un tournoi',
    createHint: 'Vous recevez un code à partager. Vous lancez le tournoi quand tout le monde est là.',
    name: 'Nom (facultatif)',
    namePlaceholder: 'Tournoi du vendredi',
    modeLabel: 'Mode de jeu',
    sizeLabel: 'Nombre de joueurs maximum',
    create: 'Créer le tournoi',
    mine: 'Vos tournois',
    back: 'Tournois',
    defaultName: (host: string) => `Tournoi de ${host}`,
    status: { registration: 'Inscriptions ouvertes', running: 'En cours', finished: 'Terminé', cancelled: 'Annulé' } as Record<TournamentStatus, string>,
    playerCount: (count: number, max: number) => `${count} / ${max} joueurs`,
    hostedBy: (name: string) => `Organisé par ${name}`,
    open: 'Ouvrir',
    codeLabel: 'Code',
    copyLink: 'Copier le lien',
    copied: 'Copié !',
    share: 'Partager',
    shared: 'Partagé !',
    shareText: (name: string, host: string) => `${host} vous invite au tournoi « ${name} » sur Chess-Uno. Rejoignez-le :`,
    players: 'Joueurs',
    host: 'Organisateur',
    you: 'vous',
    join: 'Rejoindre le tournoi',
    leave: 'Quitter le tournoi',
    start: 'Lancer le tournoi',
    startHint: 'Il faut au moins 2 joueurs. Le tableau est tiré au sort au lancement ; plus personne ne peut s’inscrire ensuite.',
    waitingHost: 'En attente du lancement par l’organisateur…',
    cancel: 'Annuler le tournoi',
    cancelConfirm: 'Annuler ce tournoi pour tous les joueurs ?',
    yourMatch: (opponent: string) => `Votre match est prêt contre ${opponent}`,
    play: 'Jouer',
    waitingNext: 'Prochain match : en attente des autres résultats…',
    byeThrough: 'Qualifié d’office pour le tour suivant.',
    eliminated: (round: string) => `Éliminé en ${round.toLowerCase()}. Suivez la suite ici.`,
    champion: 'Vainqueur du tournoi',
    youWon: 'Bravo, vous remportez le tournoi !',
    cancelled: 'Ce tournoi a été annulé par l’organisateur.',
    bracket: 'Tableau',
    /** Name of a round, counted from the end (1 = final): "Demi-finales". */
    roundName: (fromEnd: number): string =>
      ({ 1: 'Finale', 2: 'Demi-finales', 3: 'Quarts de finale', 4: 'Huitièmes de finale', 5: 'Seizièmes de finale' })[fromEnd] ?? `Tour ${fromEnd}`,
    /** Same, for one match: "Demi-finale". */
    roundOne: (fromEnd: number): string =>
      ({ 1: 'Finale', 2: 'Demi-finale', 3: 'Quart de finale', 4: 'Huitième de finale', 5: 'Seizième de finale' })[fromEnd] ?? `Tour ${fromEnd}`,
    tbd: 'À déterminer',
    bye: 'Qualifié d’office',
    matchStates: { waiting: 'En attente', playing: 'En cours', done: 'Terminé' } as Record<MatchStatus, string>,
    endedBy: {
      king_captured: 'roi capturé',
      checkmate: 'échec et mat',
      resign: 'abandon',
      abandon: 'forfait',
      draw_agreed: 'nulle acceptée',
      no_move_limit: 'nulle par inactivité',
      coin_toss: 'pile ou face',
    } as Record<MatchEnd, string>,
    redraws: (count: number) => `${n(count, 'nulle rejouée', 'nulles rejouées')}`,
    rulesTitle: 'Règles du tournoi',
    rules: (idleMinutes: number): string[] => [
      'Élimination directe : le perdant d’un match est éliminé, le gagnant passe au tour suivant.',
      'Le tableau est tiré au sort au lancement. Si le nombre de joueurs n’est pas une puissance de deux, certains sont qualifiés d’office au premier tour.',
      'Une partie nulle est rejouée avec les couleurs inversées ; après trois nulles, une pièce tranche.',
      `Pas de revanche. Un joueur qui n’agit pas pendant ${n(idleMinutes, 'minute')}, ou qui reste déconnecté, perd la partie.`,
      'Les parties de tournoi comptent pour le classement Elo.',
    ],
    notFoundTitle: 'Tournoi introuvable',
    notFoundLead: 'Vérifiez le code reçu de l’organisateur.',
    invited: 'Vous êtes invité à un tournoi',
    invitedTo: (name: ReactNode) => (
      <>
        Vous êtes invité au tournoi <strong>{name}</strong>
      </>
    ),
    toast: {
      aria: (name: string) => `Match de tournoi : ${name}`,
      ready: (opponent: ReactNode) => (
        <>
          Votre match est prêt contre <strong>{opponent}</strong>
        </>
      ),
      play: 'Jouer',
      later: 'Plus tard',
    },
    game: {
      strip: (name: string, round: string) => `${name} · ${round}`,
      rule: (idleMinutes: number) => `Pas de revanche · ${n(idleMinutes, 'minute')} d’inactivité maximum`,
      bracket: 'Voir le tableau',
      backToBracket: 'Retour au tableau',
      drawReplay: 'Nulle : le match est rejoué, couleurs inversées.',
    },
  },

  profile: {
    notFound: 'Joueur introuvable.',
    eyebrow: 'Profil',
    challenge: (name: string) => `Défier ${name}`,
    shareMe: (rank: number, rating: number) => `Je suis ${rank ? `#${rank} ` : ''}sur Chess-Uno avec ${rating} Elo. Viens me détrôner !`,
    shareOther: (name: string, rating: number) => `${name} a ${rating} Elo sur Chess-Uno. Qui relèvera le défi ?`,
    linkCopied: 'Lien copié !',
    shared: 'Partagé !',
    share: 'Partager',
    yourRanking: 'Votre classement',
    ranking: 'Classement',
    youVs: (name: string) => `Vous contre ${name}`,
    draws: (count: number) => n(count, 'nulle'),
    headToHead: 'Face-à-face',
    recentResults: 'Derniers résultats',
  },

  rules: {
    eyebrow: 'Règlement',
    toc: 'Sommaire',
  },

  legal: {
    updated: (date: string) => `Dernière mise à jour : ${date}`,
  },
}

export type Messages = typeof fr
